import {ClientLatencyTools} from '@packages/systems/designer/performance-tools';
import {TelemetryEventLabel} from '@packages/systems/designer-telemetry';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type UndoRedoHistory} from '@packages/systems/undoRedo';
import {namesEqual} from '@packages/systems/wfdl-Qualified';
import {pageComponentType} from '@packages/utilities/platform';
import {getLiveComponentMap} from '@packages/utilities/platform-selectors';

const LABEL = TelemetryEventLabel.MAIN_COMPONENT_EDIT_TO_RENDER_LATENCY;

// A commit that never re-renders the canvas would otherwise stay open until
// an unrelated canvas update closes it with an arbitrarily long sample.
export const STALE_MEASUREMENT_MS = 5000;

let staleTimer: ReturnType<typeof setTimeout> | undefined;

export type ComponentEditState = {
  DesignerStore: DesignerStateType;
  UndoRedoStore: {designer: UndoRedoHistory};
};

/**
 * A committed local edit is one that moves the designer undo history, which
 * hover previews, page loads and remote multiplayer updates never do.
 */
export const isMainComponentEditCommit = (
  prevState: ComponentEditState,
  nextState: ComponentEditState
): boolean => {
  // The canvas renders the live map, so a commit that only swaps an already
  // previewed staged map in re-renders nothing and would never finish.
  const prevComponents = getLiveComponentMap(prevState.DesignerStore);
  const nextComponents = getLiveComponentMap(nextState.DesignerStore);
  if (prevComponents === nextComponents) return false;

  const nextHistory = nextState.UndoRedoStore.designer;
  if (nextHistory === prevState.UndoRedoStore.designer) return false;
  // Page switches and library installs clear the history.
  if (nextHistory.past.size === 0 && nextHistory.future.size === 0) {
    return false;
  }

  return (
    nextComponents.find((component, name) => {
      const prevComponent = prevComponents.get(name);
      return (
        !namesEqual(name, pageComponentType) &&
        prevComponent !== undefined &&
        prevComponent !== component
      );
    }) !== undefined
  );
};

export const startMainComponentEditMeasurement = (): void => {
  // A second `start` without a `cancel` throws internally and drops the whole
  // measurement, so a rapid re-edit would otherwise report nothing.
  ClientLatencyTools.cancel(LABEL);
  ClientLatencyTools.start(LABEL);
  clearTimeout(staleTimer);
  staleTimer = setTimeout(
    () => ClientLatencyTools.cancel(LABEL),
    STALE_MEASUREMENT_MS
  );
};

export const finishMainComponentEditMeasurement = (): void => {
  clearTimeout(staleTimer);
  ClientLatencyTools.finishAndReport(LABEL);
};
